// apps/web/src/features/crm/leads/card/LeadCardPhaseBanner.tsx
//
// Phase/state banner between the header card and the body grid (#2308). Renders
// the shared `Banner` for the three states that change how the card should be
// read — postponed (warn + resume action), terminal (error, read-only), and
// DEAL (success) — and nothing otherwise. State is derived from the projection
// stage/phase (`isTerminalEngagementStage`, `EngagementStage.POSTPONED`); there
// is no reason field on the projection, so none is invented. The resume action
// routes through the reused legacy stage mutation via `onBackToWork`.
import { useTranslation } from 'react-i18next';
import {
  EngagementPhase,
  EngagementStage,
  isTerminalEngagementStage,
  type EngagementDetailView,
} from '@magimp/shared';
import { Banner, Button } from '../../../../ui/index.js';
import { formatDateTime } from '../../../../i18n/formatters.js';

export interface LeadCardPhaseBannerProps {
  detail: EngagementDetailView;
  canManage: boolean;
  onBackToWork: () => void;
}

export function LeadCardPhaseBanner({ detail, canManage, onBackToWork }: LeadCardPhaseBannerProps) {
  const { t } = useTranslation(['leads']);

  if (detail.stage === EngagementStage.POSTPONED) {
    return (
      <Banner
        tone="warn"
        icon="timer"
        action={
          canManage ? (
            <Button kind="secondary" size="sm" icon="rotate-ccw" onClick={onBackToWork}>
              {t('leads:card.banner.resume')}
            </Button>
          ) : undefined
        }
      >
        {t('leads:card.banner.postponed', { date: formatDateTime(detail.resumeAt) })}
      </Banner>
    );
  }

  if (isTerminalEngagementStage(detail.stage)) {
    return (
      <Banner tone="error" icon="x-circle">
        {detail.stage === EngagementStage.REFUSED
          ? t('leads:card.banner.terminalRefused')
          : t('leads:card.banner.terminalNonTarget')}
      </Banner>
    );
  }

  if (detail.phase === EngagementPhase.DEAL) {
    return (
      <Banner tone="success" icon="handshake">
        {detail.dealCode
          ? t('leads:card.banner.deal', { dealCode: detail.dealCode })
          : t('leads:card.banner.dealNoCode')}
      </Banner>
    );
  }

  return null;
}
